import { formatMoney, formatNumber } from "../../intl/formatters";

export function resolveCasaMapaPriceAmount(listing) {
  if (listing?.priceMoney?.amount != null) {
    return Number(listing.priceMoney.amount);
  }
  if (listing?.price != null) {
    return Number(listing.price);
  }
  return null;
}

/**
 * Format a COP-stored amount for display.
 * Prefer CurrencyProvider.formatMoney in React; this helper is for tests and
 * non-provider contexts. Pass displayCurrency (not listing.currency) so UI
 * currency selection is respected.
 */
export function formatCasaMapaMoney(amount, options = {}) {
  if (amount == null || !Number.isFinite(Number(amount)) || Number(amount) <= 0) {
    return null;
  }

  const locale = options.locale || "es-CO";
  const displayCurrency = options.displayCurrency || options.currency || "COP";
  const storageCurrency = options.storageCurrency || "COP";

  try {
    const formatted = formatMoney(Number(amount), {
      locale,
      displayCurrency,
      storageCurrency,
      rates: options.rates,
      ratesAvailable: options.ratesAvailable,
      ratesStale: options.ratesStale,
      showApproximation: options.showApproximation === true,
    });
    return formatted || null;
  } catch {
    return `${Number(amount).toLocaleString(locale)} ${displayCurrency}`;
  }
}

export function formatCasaMapaArea(value, { locale = "es-CO" } = {}) {
  if (value == null || !Number.isFinite(Number(value)) || Number(value) <= 0) {
    return null;
  }
  try {
    return `${formatNumber(Number(value), locale, { maximumFractionDigits: 0 })} m²`;
  } catch {
    return `${Number(value)} m²`;
  }
}

export function resolveCasaMapaGallery(listing) {
  let items = [];
  if (Array.isArray(listing?.gallery) && listing.gallery.length) {
    items = listing.gallery.filter((item) => item?.url);
  } else if (Array.isArray(listing?.images) && listing.images.length) {
    items = listing.images
      .map((item, index) =>
        typeof item === "string"
          ? { url: item, thumbnailUrl: item, position: index, type: "image" }
          : item
      )
      .filter((item) => item?.url);
  } else if (listing?.primaryImage?.url) {
    items = [listing.primaryImage];
  } else if (listing?.primaryImageUrl || listing?.imageUrl || listing?.media?.primaryImageUrl) {
    const url = listing.primaryImageUrl || listing.imageUrl || listing.media.primaryImageUrl;
    items = [
      {
        url,
        thumbnailUrl: listing.thumbnailUrl || listing.media?.thumbnailUrl || url,
        position: 0,
        type: "image",
      },
    ];
  }

  const seen = new Set();
  return items.filter((item) => {
    const key = String(item.url);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function casaMapaListingHasPhoto(listing) {
  return resolveCasaMapaGallery(listing).length > 0;
}

export function isCasaMapaListingUnavailable(listing) {
  const lifecycle = String(listing?.lifecycleStatus || "").toLowerCase();
  return lifecycle === "retired" || lifecycle === "unavailable";
}

export function sanitizeCasaMapaDescription(text) {
  if (!text) {
    return null;
  }
  return String(text)
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/\r\n/g, "\n")
    .trim();
}

const AMENITY_LABEL_KEYS = {
  elevator: "realEstate.casaMapa.amenity.elevator",
  pool: "realEstate.casaMapa.amenity.pool",
  gym: "realEstate.casaMapa.amenity.gym",
  doorman: "realEstate.casaMapa.amenity.doorman",
  security: "realEstate.casaMapa.amenity.security",
  balcony: "realEstate.casaMapa.amenity.balcony",
  terrace: "realEstate.casaMapa.amenity.terrace",
  storage: "realEstate.casaMapa.amenity.storage",
  visitor_parking: "realEstate.casaMapa.amenity.visitorParking",
  playground: "realEstate.casaMapa.amenity.playground",
  green_area: "realEstate.casaMapa.amenity.greenArea",
  gated_community: "realEstate.casaMapa.amenity.gatedCommunity",
  furnished: "realEstate.casaMapa.amenity.furnished",
  pets_allowed: "realEstate.casaMapa.amenity.petsAllowed",
};

export function casaMapaAmenityLabelKey(key) {
  return AMENITY_LABEL_KEYS[key] || null;
}

export function listCasaMapaListingAmenities(listing, t) {
  const amenities = Array.isArray(listing?.amenities) ? listing.amenities : [];
  const seen = new Set();
  const items = [];
  for (const amenity of amenities) {
    const key = amenity?.key || amenity;
    if (!key || seen.has(key)) {
      continue;
    }
    const labelKey = casaMapaAmenityLabelKey(key);
    const label = labelKey ? t(labelKey) : null;
    if (!label) {
      continue;
    }
    seen.add(key);
    items.push({ key, label });
  }
  return items;
}

const PROPERTY_TYPE_KEYS = {
  apartment: "realEstate.casaMapa.types.apartment",
  house: "realEstate.casaMapa.types.house",
  studio: "realEstate.casaMapa.types.studio",
  penthouse: "realEstate.casaMapa.types.penthouse",
  duplex: "realEstate.casaMapa.types.duplex",
  townhouse: "realEstate.casaMapa.types.townhouse",
  country_house: "realEstate.casaMapa.types.countryHouse",
  farm: "realEstate.casaMapa.types.farm",
  lot: "realEstate.casaMapa.types.lot",
  office: "realEstate.casaMapa.types.office",
  commercial: "realEstate.casaMapa.types.commercial",
  warehouse: "realEstate.casaMapa.types.warehouse",
  other: "realEstate.casaMapa.types.other",
  unknown: "realEstate.casaMapa.types.unknown",
};

export function casaMapaPropertyTypeLabelKey(type) {
  return PROPERTY_TYPE_KEYS[type] || PROPERTY_TYPE_KEYS.unknown;
}
